import { $t } from '@/i18n';
import { message } from '@utils/wuji-message';
import { openBranchMergeModal } from '@/components/branch/merge-modal';
import { BRANCH_API_TIMEOUT } from '@/components/branch/merge-modal/constants';
import wujiFetch from '@/utils/wujiFetch';
import { memoizedFetchGET } from '@utils/common';
import { BASE_API_PATH } from '@config/constant';
import { useRouter } from '@/router/useRouter';
import { getAppQuery } from '@utils/path';

const designBaseUrl = `${BASE_API_PATH}/xy/design/branch`;
const getUserBranchUrl = projectId => `${BASE_API_PATH}/xy/user/branch?projectid=${projectId}`;

export const MASTER_BRANCH = 'master';
const DEFAULT_BRANCH = {
  branch: MASTER_BRANCH,
  name: MASTER_BRANCH,
  id: MASTER_BRANCH,
  isDefault: true,
};

// 分支相关接口超时时间
const branchTimeout = 60 * 1000;

const EMPTY_BEHIND_STATUS = { behind: false, behindCount: 0 };

// 同一分支的落后状态请求合并成一次：拉取按钮在分支下拉和工作台两处都会渲染
let behindStatusInflight = { key: '', promise: null };

export default {
  state: {
    currentBranch: null,
    uncommitChanges: {},
    commitList: [],
    branchList: [],
    behindStatus: { ...EMPTY_BEHIND_STATUS },
  },
  actions: {
    // 创建分支
    async createBranch({ rootState }, data) {
      const name = data.name.trim();
      if (!name) throw new Error($t('k_1b53325a'));
      const result = await wujiFetch(`${designBaseUrl}?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          name,
          checkoutBranchId: 'master',
        }),
        timeout: branchTimeout,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    // 获取当前应用的所有分支
    async fetchBranchList({ commit, rootState }, { projectId, includeArchived = 0 }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}?projectid=${projectId || rootState.project.projectInfo?.id}&includeArchived=${includeArchived}`);
        commit('updateBranchList', result);
        return result;
      } catch (err) {
        message.error($t('k_b8fdcc77'));
      }
    },
    async fetchBranchByName({}, { projectId, name }) {
      return await wujiFetch(`${designBaseUrl}/git?projectid=${projectId}&name=${name}`);
    },
    async fetchBranchInfo({ rootState }, { branchId }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}/${branchId}?projectid=${rootState.project.projectInfo?.id}`);
        return result;
      } catch (err) {
        message.error($t('k_28718a38'));
      }
    },

    async deleteBranch({ commit, state, rootState }, { branchId }) {
      await wujiFetch(`${designBaseUrl}/${branchId}?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'DELETE',
      });
      commit('updateBranchList', [...state.branchList.filter(item => item.branch !== branchId)]);
    },
    // 获取用户正在开发中的分支
    async fetchCurrentBranch({ commit }, { projectId } = {}) {
      try {
        // 优先取query的branch
        try {
          const branchQuery = getAppQuery('branch');
          if (branchQuery) {
            const branchInfo = await wujiFetch(`${designBaseUrl}/${branchQuery}?projectid=${projectId}`);
            if (branchInfo) {
              commit('updateCurrentBranch', branchInfo);
              return branchInfo;
            }
          }
        } catch (err) {
          message.warn($t('k_78c193e9'));
        }
        // query的branch无效再取用户上次开发的分支
        const result = await memoizedFetchGET(getUserBranchUrl(projectId));
        const branch = result ? result.branch : DEFAULT_BRANCH;
        commit('updateCurrentBranch', branch);
        return branch;
      } catch (err) {
        console.error(err);
        message.error($t('k_931968db'));
      }
    },
    // 切换分支
    async checkoutBranch({ rootState }, { branch, name }) {
      try {
        const projectId = rootState.project.projectInfo?.id;
        const result = await wujiFetch(`${BASE_API_PATH}/xy/user/branch/checkout/${branch}?projectid=${projectId}`, {
          method: 'POST',
        });
        if (result) {
          const { router, route } = useRouter();
          const currentQuery = route.value.query;
          // 一次性自动发送参数，切分支时不要再带上
          const { prompt, input, ...restQuery } = currentQuery;
          const { href = '' } = router.value.resolve({
            path: route.value.path,
            query: {
              ...restQuery,
              projectid: projectId,
              branch,
            },
          });
          if (name) message.success($t('k_f1727d44', { arg_0: name }));
          window.location.href = href;
        } else {
          message.error($t('k_580147f2'));
        }
      } catch (err) {
        console.error(err);
        message.error($t('k_580147f2'));
      }
    },
    // 获取分支未提交的变更
    async fetchBranchUncommitChanges({ commit, state, rootState }, { projectId, branchId } = {}) {
      try {
        const proId = projectId || rootState.project.projectInfo?.id;
        const branch = branchId || state.currentBranch?.branch;
        const result = await wujiFetch(`${designBaseUrl}/${branch}/uncommitchanges?projectid=${proId}`, {
          timeout: branchTimeout,
        });
        if (!branchId || state.currentBranch?.branch === branchId) {
          commit('updateUncommitChanges', result);
        }
        return result;
      } catch (err) {
        message.error($t('k_d1f925cf'));
      }
    },
    // 提交分支变更
    async commitBranchChanges({ rootState, dispatch }, { branchId, data }) {
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/commit?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      dispatch('fetchBranchUncommitChanges', { branchId });
      return result;
    },
    // 获取分支的提交记录
    async fetchBranchCommits({ rootState, commit }, { branchId, projectId, page = 1, size = 20 }) {
      try {
        const url = `${designBaseUrl}/${branchId}/commit?projectid=${projectId || rootState.project.projectInfo?.id}`;
        const getTotal = wujiFetch(`${url}&count=1`);
        const getList = wujiFetch(
          `${url}&page=${page}&size=${size}&sort=_ctime&order=desc`,
          { method: 'GET', timeout: branchTimeout },
        );
        const [result, { total }] = await Promise.all([getList, getTotal]);
        commit('updateCommitList', result);
        return {
          page,
          total,
          result,
        };
      } catch (err) {
        message.error($t('k_e76f0e39'));
      }
    },
    // 获取分支的最新的提交
    async fetchBranchHeadCommit({ rootState }, { branchId, projectId }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}/${branchId}/commit/head?projectid=${projectId || rootState.project.projectInfo?.id}`);
        return result;
      } catch (err) {
        message.error($t('k_88e18551'));
      }
    },
    // 撤销分支上的某条变更
    async discardBranchChange({ rootState, dispatch }, { branchId, data }) {
      const projectId = rootState.project.projectInfo.id;
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/discard?projectid=${projectId}`, {
        method: 'POST',
        body: JSON.stringify({
          data,
        }),
      });
      dispatch('fetchBranchUncommitChanges', { branchId });
      return result;
    },

    // 获取可拉取的最新commitId
    async getPullCommitId({ rootState, state }, { projectId, branchId, targetBranchId } = {}) {
      try {
        const proId = projectId || rootState.project.projectInfo?.id;
        const branch = branchId || state.currentBranch?.branch;
        const result = await wujiFetch(`${designBaseUrl}/${branch}/pullcommit?projectid=${proId}&targetbranch=${targetBranchId}`, {
          timeout: BRANCH_API_TIMEOUT,
        });
        return result;
      } catch (err) {
        message.error($t('k_50654f3b', { arg_0: err.message }));
        throw err;
      }
    },
    // 只读：当前分支是否落后 master 及落后提交数（无自动提交副作用）
    // 接口的目标分支固定为 master，不接受外部指定
    async fetchBehindStatus({ commit, rootState, state }, { projectId, branchId } = {}) {
      const proId = projectId || rootState.project.projectInfo?.id;
      const branch = branchId || state.currentBranch?.branch;
      if (!proId || !branch || branch === MASTER_BRANCH) {
        commit('updateBehindStatus', { ...EMPTY_BEHIND_STATUS });
        return { ...EMPTY_BEHIND_STATUS };
      }
      const requestKey = `${proId}:${branch}`;
      if (behindStatusInflight.key !== requestKey) {
        behindStatusInflight = {
          key: requestKey,
          promise: (async () => {
            try {
              const result = await wujiFetch(`${designBaseUrl}/${branch}/behind?projectid=${proId}`, {
                timeout: BRANCH_API_TIMEOUT,
              });
              return {
                behind: Boolean(result?.behind),
                behindCount: Number(result?.behindCount) || 0,
              };
            } catch (err) {
              // 提示用接口失败时静默，避免打扰正常编辑
              return { ...EMPTY_BEHIND_STATUS };
            }
          })(),
        };
      }
      const pending = behindStatusInflight;
      const status = await pending.promise;
      if (behindStatusInflight === pending) {
        behindStatusInflight = { key: '', promise: null };
      }
      // 分支已经切走时丢弃过期响应
      if (!branchId || state.currentBranch?.branch === branch) {
        commit('updateBehindStatus', status);
      }
      return status;
    },
    // 拉取 master 到指定分支，并把「有没有可拉取的提交」的三种结论统一同步到红点状态
    async pullMasterIntoBranch({ dispatch, commit, state }, { branchId } = {}) {
      const branch = branchId || state.currentBranch?.branch;
      if (!branch || branch === MASTER_BRANCH) {
        return { ...EMPTY_BEHIND_STATUS };
      }
      const pullCommit = await dispatch('getPullCommitId', { branchId: branch, targetBranchId: MASTER_BRANCH });
      if (!pullCommit) {
        commit('updateBehindStatus', { ...EMPTY_BEHIND_STATUS });
        message.warn($t('k_33583bab'));
        return { ...EMPTY_BEHIND_STATUS };
      }
      const { hasDiff } = await dispatch('mergeBranch', { branchId: branch, mergeBranchId: MASTER_BRANCH });
      // 服务端按内容 diff 判定无变更：master 的 commit 位置虽然前进了，
      // 但对本分支没有新内容，红点必须跟着清掉，否则会一直亮着且每次点击都提示无变更
      if (!hasDiff) {
        commit('updateBehindStatus', { ...EMPTY_BEHIND_STATUS });
        return { ...EMPTY_BEHIND_STATUS };
      }
      // 合并成功会整页刷新；用户取消合并则需要重新对齐一次状态
      return await dispatch('fetchBehindStatus', { branchId: branch });
    },
    // 获取两个分支的基准commit
    async getBaseCommit({ rootState }, { branchId, targetBranchId } = {}) {
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/basecommit?projectid=${rootState.project.projectInfo?.id}&targetbranch=${targetBranchId}`, {
        timeout: BRANCH_API_TIMEOUT,
      });
      return result;
    },
    // 获取差异
    async fetchCommitDiff({ rootState }, { commitId1, commitId2, baseCommitId }) {
      const proId = rootState.project.projectInfo?.id;
      const commits = [commitId1, commitId2, baseCommitId].filter(item => !!item).join(',');
      const result = await wujiFetch(`${designBaseUrl}/commit/diff?projectid=${proId}&commits=${commits}`, {
        timeout: BRANCH_API_TIMEOUT,
      });
      return result;
    },

    async mergeBranch({ rootState }, { branchId, mergeBranchId, disableClose }) {
      try {
        const proId = rootState.project.projectInfo?.id;
        const res = await wujiFetch(`${designBaseUrl}/${branchId}/conflict/${mergeBranchId}?projectid=${proId}`, {
          method: 'GET',
          timeout: BRANCH_API_TIMEOUT,
        });
        if (!res.diff) {
          if (!disableClose) {
            message.warn($t('k_1768046a', { arg_0: mergeBranchId === MASTER_BRANCH ? $t('k_1c675f24') : $t('k_2952c591') }));
          }
          return { hasDiff: false, success: false };
        }
        const result = await openBranchMergeModal({
          projectId: proId,
          branchId,
          mergeBranchId,
          changes: res.data,
          disableClose: !!disableClose,
        });
        return { hasDiff: true, success: !!result?.success };
      } catch (error) {
        message.error($t('k_877674a6', { arg_0: error.message }));
        throw error;
      }
    },

    /**
     * 只读预览合入 diff，不打开合并弹窗。失败返回 null，不弹错误。
     */
    async fetchMergeChangesPreview({ rootState, state }, { branchId, mergeBranchId } = {}) {
      try {
        const proId = rootState.project.projectInfo?.id;
        const currentBranch = mergeBranchId || state.currentBranch?.branch;
        const targetBranch = branchId || MASTER_BRANCH;
        if (!proId || !currentBranch) return null;
        const pullCommit = await wujiFetch(`${designBaseUrl}/${targetBranch}/pullcommit?projectid=${proId}&targetbranch=${currentBranch}`, {
          timeout: BRANCH_API_TIMEOUT,
        });
        if (!pullCommit) return null;
        const res = await wujiFetch(`${designBaseUrl}/${targetBranch}/conflict/${currentBranch}?projectid=${proId}`, {
          method: 'GET',
          timeout: BRANCH_API_TIMEOUT,
        });
        if (!res?.diff) return null;
        return res.data || null;
      } catch {
        return null;
      }
    },

    async fetchCommitChanges({ rootState }, { commitId, branchId }) {
      const projectId = rootState.project.projectInfo.id;
      try {
        return await wujiFetch(`${designBaseUrl}/${branchId}/commit/${commitId}?projectid=${projectId || rootState.project.projectInfo?.id}`);
      } catch (err) {
        message.error($t('k_e76f0e39'));
      }
    },

    async resetCommitPreview({ rootState }, { commitId, branchId, projectId }) {
      const proId = projectId || rootState.project.projectInfo?.id;
      return await wujiFetch(`${designBaseUrl}/${branchId}/commit/reset/${commitId}/preview?projectid=${proId}`, {
        method: 'GET',
        timeout: branchTimeout,
      });
    },

    async resetCommit({ rootState }, { commitId, branchId, projectId }) {
      try {
        return await wujiFetch(`${designBaseUrl}/${branchId}/commit/reset/${commitId}?projectid=${projectId || rootState.project.projectInfo?.id}`, {
          method: 'POST',
        });
      } catch (err) {
        message.error($t('k_c1fc01d2'));
        throw err;
      }
    },
  },
  mutations: {
    resetBranch(state) {
      state.currentBranch = null;
    },
    updateBranchList(state, data) {
      state.branchList = data;
    },
    updateCommitList(state, data) {
      state.commitList = data;
    },
    updateCurrentBranch(state, data) {
      state.currentBranch = data;
    },
    updateUncommitChanges(state, data) {
      state.uncommitChanges = data;
    },
    updateBehindStatus(state, data) {
      state.behindStatus = data;
    },
  },
};
